import type { MessageView, RequestView } from "@walkie/protocol"
import { memo, useLayoutEffect, useRef, useState } from "react"
import { agentColor } from "../lib/agents"
import { isWithdrawn, oneLine, withdrawnAt, withdrawnBy } from "../lib/messages"
import { shortId } from "../lib/refs"
import { age, clock, fullTime } from "../lib/time"
import {
	canWithdraw,
	citeInComposer,
	claim,
	discardSend,
	jumpToMessage,
	openThread,
	release,
	retrySend,
} from "../store/actions"
import { getState, setUi, useStore } from "../store/store"
import { DeliveryGlyphs } from "./DeliveryGlyphs"
import { Author, Body, HandleText, KindBadge, Ref, Tip } from "./ui"

export const MessageRow = memo(function MessageRow(props: {
	m: MessageView
	selected: boolean
	cont?: boolean
	inThread?: boolean
}) {
	const { m, selected } = props
	const sending = useStore(s => s.sending[m.id])
	const canWrite = useStore(s => s.role !== "viewer")
	const mayWithdraw = useStore(s => canWithdraw(m, s))
	if (isWithdrawn(m))
		return <Tombstone m={m} selected={selected} inThread={props.inThread} />
	const local = m.id.startsWith("local:")
	const urgent =
		m.priority === "urgent" &&
		(!m.request || m.request.status === "open" || m.request.status === "claimed")
	const cls = [
		"msg",
		urgent && "urgent",
		m.supersededBy && "superseded",
		m.supersededBy && !m.title && "strike-body",
		local && "local",
		props.cont && !m.request && "cont",
	]
		.filter(Boolean)
		.join(" ")

	return (
		<div
			id={`m-${m.id}`}
			className={cls}
			role="option"
			aria-selected={selected}
			tabIndex={-1}
			onMouseDown={e => {
				if ((e.target as HTMLElement).closest("button, a, input, textarea, select"))
					return
				if (!props.inThread) setUi({ selectedId: m.id })
			}}
		>
			<Tip content={fullTime(m.createdAt)}>
				<time className="msg-time mono" dateTime={m.createdAt}>
					{clock(m.createdAt)}
				</time>
			</Tip>
			<div className="msg-main">
				<div className="msg-head">
					<Author m={m} />
					{urgent && <span className="kind k-urgent">urgent</span>}
					<KindBadge kind={m.kind} />
					{m.title && <span className="msg-title ell">{m.title}</span>}
					<Recipients m={m} />
					{m.request && <ReqStatus r={m.request} />}
				</div>
				<ClampedBody text={m.body} />
				{m.cites.length > 0 && <CiteLines ids={m.cites} />}
				<div className="msg-foot">
					{m.supersededBy && <SupersedeLink id={m.supersededBy} label="superseded by" />}
					{m.supersedes && <SupersedeLink id={m.supersedes} label="supersedes" />}
					{m.refs
						.filter(
							r =>
								!m.request?.evidence.some(e => e.ref === r) &&
								!m.request?.deps.some(d => d.target === r),
						)
						.map(r => (
							<Ref key={r} value={r} />
						))}
					{m.request?.deps.map(d => (
						<span
							key={d.kind + d.target}
							className={"dep" + (d.satisfiedAt ? " ok" : "")}
							title={d.kind.replace("_", " ")}
						>
							{d.satisfiedAt ? "✓" : "◌"} {d.target}
						</span>
					))}
					{m.request?.evidence.map((e, i) => (
						<span key={i} className="evidence">
							<span className="dim">{e.kind}</span>
							<Ref value={e.ref} />
							{e.verified && (
								<span className="v" aria-label="verified">
									✓
								</span>
							)}
						</span>
					))}
					{m.request?.resolution && (
						<span className="dim2 ell" style={{ fontSize: 11 }}>
							↳ {m.request.resolution}
						</span>
					)}
					{!props.inThread && m.replyCount > 0 && (
						<button
							className="thread-link"
							onClick={() => openThread(m.id)}
							aria-label={`Open thread, ${m.replyCount} replies`}
						>
							{m.replyCount} {m.replyCount === 1 ? "reply" : "replies"}
							{m.lastReplyAt && (
								<span className="dim">· {age(m.lastReplyAt, Date.now())}</span>
							)}
						</button>
					)}
				</div>
				{sending && (
					<div
						className="send-fail"
						role={sending.status === "failed" ? "alert" : undefined}
					>
						{sending.status === "sending" ? (
							<span className="dim">sending…</span>
						) : (
							<>
								<span>Not sent: {sending.error}</span>
								<button className="btn btn-xs" onClick={() => retrySend(m.id)}>
									Retry
								</button>
								<button
									className="btn btn-xs btn-ghost"
									onClick={() => discardSend(m.id)}
								>
									Discard
								</button>
							</>
						)}
					</div>
				)}
			</div>
			<div className="msg-side">
				{m.deliveries && <DeliveryGlyphs deliveries={m.deliveries} />}
			</div>
			{!local && (
				<div className="msg-actions">
					{!props.inThread && (
						<button
							className="btn btn-xs btn-ghost"
							onClick={() => openThread(m.id, true)}
							aria-label="Reply in thread"
						>
							Reply <kbd>r</kbd>
						</button>
					)}
					{canWrite && m.request?.status === "open" && (
						<button className="btn btn-xs btn-ghost" onClick={() => claim(m.id)}>
							Claim <kbd>c</kbd>
						</button>
					)}
					{canWrite && m.request?.status === "claimed" && (
						<button className="btn btn-xs btn-ghost" onClick={() => release(m.id)}>
							Release
						</button>
					)}
					{canWrite &&
						(m.request?.status === "open" || m.request?.status === "claimed") && (
							<button
								className="btn btn-xs btn-ghost"
								onClick={() => setUi({ resolveFor: m.id })}
							>
								Resolve <kbd>e</kbd>
							</button>
						)}
					{canWrite && (
						<button
							className="btn btn-xs btn-ghost"
							onClick={() => citeInComposer(m.id)}
							aria-label="Cite this message in the composer"
						>
							Cite
						</button>
					)}
					{mayWithdraw && (
						<button
							className="btn btn-xs btn-ghost btn-danger"
							onClick={() => setUi({ withdrawFor: m.id })}
							aria-label="Withdraw this message"
						>
							Withdraw <kbd>w</kbd>
						</button>
					)}
					<Tip content={`id ${m.id}`}>
						<button
							className="btn btn-xs btn-ghost mono"
							aria-label={`Copy message id ${shortId(m.id)}`}
							onClick={() => void navigator.clipboard.writeText(shortId(m.id))}
						>
							{shortId(m.id)}
						</button>
					</Tip>
				</div>
			)}
		</div>
	)
})

function Recipients({ m }: { m: MessageView }) {
	const agents = useStore(s => s.agents)
	const people = useStore(s => s.people)
	if (!m.toAgents.length && !m.toUsers.length) return null
	return (
		<span className="to">
			→{" "}
			{m.toAgents.map(id => {
				const a = agents[id]
				return (
					<span
						key={id}
						style={{ color: a ? agentColor(a.color) : undefined, marginRight: 4 }}
					>
						@{a?.handle ?? shortId(id)}
					</span>
				)
			})}
			{m.toUsers.map(id => (
				<span key={id} style={{ marginRight: 4, color: "var(--text-2)" }}>
					@{people.find(p => p.userId === id)?.handle ?? shortId(id)}
				</span>
			))}
		</span>
	)
}

export function ReqStatus({ r }: { r: RequestView }) {
	if (r.status === "open")
		return (
			<span className="status open">
				● open
				{(r.ownerAgentId || r.ownerUserId) && (
					<>
						<span className="dim"> · owner</span>{" "}
						<HandleText id={r.ownerAgentId ?? r.ownerUserId} />
					</>
				)}
			</span>
		)
	if (r.status === "claimed")
		return (
			<span className="status claimed">
				◐ claimed by <HandleText id={r.claimedByAgentId ?? r.claimedByUserId} />
				{r.eta && <span className="dim">· eta {r.eta}</span>}
			</span>
		)
	if (r.status === "resolved") return <span className="status resolved">✓ resolved</span>
	return <span className="status">✕ cancelled</span>
}

function ClampedBody({ text }: { text: string }) {
	const ref = useRef<HTMLDivElement>(null)
	const [open, setOpen] = useState(false)
	const [over, setOver] = useState(false)
	useLayoutEffect(() => {
		const el = ref.current
		if (!el || open) return
		setOver(el.scrollHeight > el.clientHeight + 2)
	}, [text, open])
	return (
		<>
			<div ref={ref} className={"msg-body" + (open ? "" : " clamp")}>
				<Body text={text} />
			</div>
			{(over || open) && (
				<button className="more" onClick={() => setOpen(!open)} aria-expanded={open}>
					{open ? "show less" : "show more"}
				</button>
			)}
		</>
	)
}

/** A withdrawn message keeps its place (and its thread) as a dim one-liner. */
function Tombstone(props: { m: MessageView; selected: boolean; inThread?: boolean }) {
	const { m } = props
	const people = useStore(s => s.people)
	const at = withdrawnAt(m) ?? m.createdAt
	const by = withdrawnBy(m, people)
	const cancelled = m.request?.status === "cancelled"
	return (
		<div
			id={`m-${m.id}`}
			className="msg withdrawn"
			role="option"
			aria-selected={props.selected}
			tabIndex={-1}
			onMouseDown={e => {
				if ((e.target as HTMLElement).closest("button, a")) return
				if (!props.inThread) setUi({ selectedId: m.id })
			}}
		>
			<Tip content={fullTime(m.createdAt)}>
				<time className="msg-time mono" dateTime={m.createdAt}>
					{clock(m.createdAt)}
				</time>
			</Tip>
			<div className="msg-main">
				<div className="tomb">
					<span aria-hidden>⌀</span>
					<span>
						message withdrawn by <b>@{by}</b> ·{" "}
						<Tip content={fullTime(at)}>
							<time dateTime={at}>{clock(at)}</time>
						</Tip>
					</span>
					{m.request && cancelled && (
						<span className="status">✕ request cancelled</span>
					)}
					{!props.inThread && m.replyCount > 0 && (
						<button
							className="thread-link"
							onClick={() => openThread(m.id)}
							aria-label={`Open thread, ${m.replyCount} replies`}
						>
							{m.replyCount} {m.replyCount === 1 ? "reply" : "replies"}
						</button>
					)}
				</div>
			</div>
			<div className="msg-side" />
		</div>
	)
}

/** One line per cited message: kind, title, author, state, SUPERSEDED. */
function CiteLines({ ids }: { ids: string[] }) {
	const messages = useStore(s => s.messages)
	const agents = useStore(s => s.agents)
	return (
		<ul className="cites" aria-label="Cites">
			{ids.map(id => {
				const c = messages[id]
				if (!c)
					return (
						<li key={id} className="cite">
							<span className="cite-mark" aria-hidden>
								↗
							</span>
							<span className="dim mono">{shortId(id)}</span>
							<span className="dim">not loaded</span>
						</li>
					)
				const a = c.author.type === "agent" && c.author.id ? agents[c.author.id] : null
				const gone = isWithdrawn(c)
				return (
					<li key={id} className="cite">
						<span className="cite-mark" aria-hidden>
							↗
						</span>
						<KindBadge kind={c.kind} />
						<button
							className={"cite-title ell" + (c.supersededBy ? " struck" : "")}
							onClick={() => jumpToMessage(c)}
							title={gone ? "withdrawn" : c.body}
						>
							{gone ? "message withdrawn" : oneLine(c)}
						</button>
						<span
							className="cite-who"
							style={{ color: a ? agentColor(a.color) : undefined }}
						>
							@{c.author.handle}
						</span>
						{c.request && <ReqStatusShort r={c.request} />}
						{c.supersededBy && <span className="kind k-superseded">superseded</span>}
					</li>
				)
			})}
		</ul>
	)
}

function SupersedeLink({ id, label }: { id: string; label: string }) {
	const target = useStore(s => s.messages[id])
	return (
		<span className={"supersede-note" + (label === "supersedes" ? " fwd" : "")}>
			{label}{" "}
			<button
				className="link"
				onClick={() => {
					const t = getState().messages[id]
					if (t) jumpToMessage(t)
				}}
				disabled={!target}
				title={target ? target.body : `message ${shortId(id)} (not loaded)`}
			>
				{target ? oneLine(target, 80) || shortId(id) : shortId(id)}
			</button>
		</span>
	)
}

function ReqStatusShort({ r }: { r: RequestView }) {
	const map = {
		open: ["open", "status open"],
		claimed: ["claimed", "status claimed"],
		resolved: ["✓ resolved", "status resolved"],
		cancelled: ["✕ cancelled", "status"],
	} as const
	const [text, cls] = map[r.status]
	return <span className={cls}>{text}</span>
}
